// Must run before React/drei/the engine create any ResizeObserver.
import "@/lib/resizeObserverFix";
import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
// Original prototype design system (ported verbatim from public/lhc-app.html)
// + the React shell glue. Imported AFTER index.css so it wins over Tailwind.
import "@/styles/lhc-prototype.css";
import "@/styles/lhc-shell.css";
import App from "@/App";
import { startBuildWatcher } from "@/lib/buildWatcher";

/* STALE-DEPLOY RECOVERY (2026-08-19 user: "when I click 3D it isn't working"):
 * after a deploy swaps the build, a browser still holding the OLD index/main
 * asks for chunk files that no longer exist — the first lazy view (3D, /admin,
 * …) then dies with a ChunkLoadError. Reload ONCE to pick up the new build;
 * the sessionStorage guard prevents a reload loop if something else is wrong. */
const _isChunkError = (msg) => /loading (css )?chunk [\w-]+ failed|chunkloaderror|failed to fetch dynamically imported module/i.test(String(msg || ""));
const _chunkReload = () => {
  try {
    if (sessionStorage.getItem("lhc_chunk_reload")) return;
    sessionStorage.setItem("lhc_chunk_reload", String(Date.now()));
    window.location.reload();
  } catch (e) { /* storage unavailable — let the error surface */ }
};
window.addEventListener("error", (e) => { if (_isChunkError(e && (e.message || (e.error && e.error.message)))) _chunkReload(); });
window.addEventListener("unhandledrejection", (e) => {
  const r = e && e.reason;
  if (_isChunkError(r && (r.message || r.name === "ChunkLoadError" ? (r.message || "chunkloaderror") : ""))) _chunkReload();
});
// clear the guard only once this load has SURVIVED a while — clearing at boot
// would re-arm an infinite reload loop when the failure is permanent
setTimeout(() => { try { sessionStorage.removeItem("lhc_chunk_reload"); } catch (e) { /* ok */ } }, 15000);

/* A tab left open runs its ORIGINAL javascript forever — shipped fixes then
 * look like they never happened. The watcher notices a new deployed build and
 * offers/applies the update (never while __LHC_BUSY). */
startBuildWatcher();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
